import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Microscope, Cpu, Zap, Shield } from 'lucide-react';
import ScrollReveal from '@/components/animations/ScrollReveal';
import { useContent } from '@/hooks/useContent';

gsap.registerPlugin(ScrollTrigger);

const Technology = () => {
  const { t } = useContent();
  const sectionRef = useRef<HTMLDivElement>(null);

  const equipment = [
    {
      nameKey: 'technology.automatedAnalyzer.title',
      descriptionKey: 'technology.automatedAnalyzer.description',
      icon: Microscope,
    },
    {
      nameKey: 'technology.digitalImaging.title',
      descriptionKey: 'technology.digitalImaging.description',
      icon: Cpu,
    },
    {
      nameKey: 'technology.qualityControl.title',
      descriptionKey: 'technology.qualityControl.description',
      icon: Shield,
    },
    {
      nameKey: 'technology.highSpeed.title',
      descriptionKey: 'technology.highSpeed.description',
      icon: Zap,
    },
  ];

  useEffect(() => {
    document.title = `${t('common.technology')} - ElhamyLabs`;
  }, [t]);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-brand-purple-dark via-brand-purple-base to-brand-purple-light dark:from-dark-primary dark:via-dark-secondary">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              {t('technology.title')}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              {t('technology.subtitle')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Equipment Section */}
      <section ref={sectionRef} className="py-20 bg-light-primary dark:bg-dark-primary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
            {equipment.map((item, index) => {
              const Icon = item.icon;
              return (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="p-8 bg-light-secondary dark:bg-dark-secondary rounded-xl">
                    <div className="w-16 h-16 mb-6 rounded-full bg-brand-purple-base/10 dark:bg-brand-orange-base/10 flex items-center justify-center">
                      <Icon size={32} className="text-brand-purple-base dark:text-brand-orange-base" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4 text-light-text dark:text-dark-text">
                      {t(item.nameKey)}
                    </h3>
                    <p className="text-light-text-secondary dark:text-dark-text-secondary">
                      {t(item.descriptionKey)}
                    </p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>

          {/* Workflow Section */}
          <ScrollReveal>
            <div className="bg-light-secondary dark:bg-dark-secondary rounded-xl p-12">
              <h2 className="text-4xl font-bold mb-8 text-center text-light-text dark:text-dark-text">
                {t('technology.workflow.title')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {[
                  { step: '1', titleKey: 'technology.workflow.sampleCollection.title', descriptionKey: 'technology.workflow.sampleCollection.description' },
                  { step: '2', titleKey: 'technology.workflow.processing.title', descriptionKey: 'technology.workflow.processing.description' },
                  { step: '3', titleKey: 'technology.workflow.qualityControl.title', descriptionKey: 'technology.workflow.qualityControl.description' },
                  { step: '4', titleKey: 'technology.workflow.results.title', descriptionKey: 'technology.workflow.results.description' },
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-orange-base text-white flex items-center justify-center text-2xl font-bold">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-semibold mb-2 text-light-text dark:text-dark-text">
                      {t(item.titleKey)}
                    </h3>
                    <p className="text-light-text-secondary dark:text-dark-text-secondary">
                      {t(item.descriptionKey)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
};

export default Technology;

